// Phase 3 of docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_PLAN.md: "used by N
// mapping(s)" on the Templates screen, so an owner can see what's actually
// feeding a signal before renaming/renumbering it.
//
// No new backend read-model or reverse index - the plan's own bullet asked
// to check for a cheap existing way first, and there's an even cheaper one
// than internal/integration's read-model (itself only ever owner-scoped,
// via `mapping:index:mine:<owner>` - see that package's List): the
// frontend already loads the caller's own Mappings into the store
// (StoreContext's `mappings`, from GET /mappings) for other screens, so
// this is a plain client-side scan over data already in memory - no
// request at all.
//
// Deliberately undercounts, not overcounts, in two ways worth knowing:
//  - Only the caller's OWN mappings are ever visible to scan (signalRef
//    resolution is by global number - internal/templatesignal.Resolve has
//    no owner scoping at all - so another account's mapping could target
//    this same number and never show up here). Framed as "your mappings"
//    in the UI, not a global count, for exactly this reason.
//  - Only a static `const` ref is counted. A dynamic `from`-sourced ref
//    (Phase 4's per-record Salesforce case) could resolve to any number
//    depending on the data it sees - there's no sample record here to
//    evaluate it against, and guessing would be worse than not counting.
import type { Mapping } from '../types';
import type { MappingRule } from './mappingEngine';

function rulesOf(spec: Record<string, unknown>): MappingRule[][] {
  const outputs = (spec as { outputs?: unknown }).outputs;
  if (Array.isArray(outputs)) {
    return outputs.map((o) => (o && typeof o === 'object' && Array.isArray((o as { rules?: unknown }).rules) ? ((o as { rules: MappingRule[] }).rules) : []));
  }
  const rules = (spec as { rules?: unknown }).rules;
  return [Array.isArray(rules) ? (rules as MappingRule[]) : []];
}

export function mappingTargetsSignalNumber(spec: Record<string, unknown>, number: string): boolean {
  if (!number) return false;
  return rulesOf(spec).some((rules) => rules.some((r) => r.to === 'signalRef' && r.const !== undefined && String(r.const) === number));
}

// mappings is typed as always an array (StoreContext.tsx), but it's backed
// by a query-cache read (lib/queryClient.ts's useQueryState) that can hand
// back undefined for a brief window if that query gets garbage-collected
// between renders - confirmed live (TemplateManageScreen crashed here with
// "e is undefined" right as a mapping edit elsewhere triggered a
// syncMappings() refresh). This is the one place that read genuinely
// crosses that boundary, so it's the one place guarding against it.
export function countMappingsTargetingSignal(mappings: Mapping[] | undefined | null, number: string): number {
  if (!number || !mappings) return 0;
  return mappings.filter((m) => mappingTargetsSignalNumber(m.spec, number)).length;
}
